import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ChecklistQuery, ChecklistResponse } from '@troia-v3/types';

import { ListPageEmpty, ListPageLoading, PageToolbar, StatusPill } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useAuth } from '@/contexts/AuthContext';
import { useUnitsDropdown } from '@/shared/hooks/useUnitsDropdown';

import { useMyChecklists } from '../hooks/useChecklists';
import { CHECKLIST_STATUS_META } from './ChecklistsPage';

/** Status em que o responsável ainda PREENCHE (os demais são leitura). */
const OPEN_STATUSES: ReadonlySet<ChecklistResponse['status']> = new Set(['pending', 'in_progress']);

/**
 * `GET /checklists/my` é paginado e o default do backend é 20 por página.
 *
 * Com 20, um `pending` antigo some da caixa em poucos dias, empurrado por
 * aprovados/reprovados recentes — o operador perde de vista justamente o que
 * ainda precisa fazer. Subimos para 100 e particionamos client-side em
 * "A fazer" / "Histórico".
 *
 * DÍVIDA CONHECIDA: isso é um teto, não paginação. Acima de 100 itens o rodapé
 * mostra "Mostrando X de N" e o excedente fica inacessível por esta tela. A
 * solução completa é o backend aceitar filtro de status em `/my` (aí "A fazer"
 * vira uma query própria, sempre completa) + paginação no histórico.
 *
 * Referência de módulo (queryKey do React Query) — precisa ser estável.
 * Só CHECKLIST: aplicações de formulário atribuídas ao usuário não entram aqui
 * (mesma regra da listagem de gestão, `ChecklistsPage`).
 */
const MY_CHECKLISTS_QUERY: ChecklistQuery = { limit: 100, filters: { templateType: 'checklist' } };

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

type CountdownTone = 'urgent' | 'normal' | 'muted';

/** "2h 15min", "3 dias", "45min" — duração absoluta, sem sufixo. */
const formatDuration = (ms: number): string => {
  if (ms < HOUR_MS) return `${Math.max(Math.floor(ms / MINUTE_MS), 1)}min`;
  if (ms < DAY_MS) {
    const hours = Math.floor(ms / HOUR_MS);
    const minutes = Math.floor((ms % HOUR_MS) / MINUTE_MS);
    return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
  }
  const days = Math.floor(ms / DAY_MS);
  return days === 1 ? '1 dia' : `${days} dias`;
};

/**
 * Contagem regressiva do prazo. `now` vem por parâmetro (e não de `Date.now()`
 * dentro da função) porque a página re-renderiza de minuto em minuto com um
 * `now` novo — é o que faz o contador ANDAR sozinho na tela do gerente sem
 * precisar de refetch.
 *
 * Vermelho abaixo de 1h ou já vencido: é o limiar em que ele ainda consegue
 * correr até a unidade e resolver.
 */
export const formatCountdown = (
  expiresAt: string | undefined,
  now: number,
): { label: string; tone: CountdownTone } => {
  if (!expiresAt) return { label: 'Sem prazo', tone: 'muted' };
  const due = new Date(expiresAt).getTime();
  if (Number.isNaN(due)) return { label: 'Sem prazo', tone: 'muted' };

  const remaining = due - now;
  if (remaining <= 0) return { label: `Atrasado há ${formatDuration(-remaining)}`, tone: 'urgent' };
  return { label: `Vence em ${formatDuration(remaining)}`, tone: remaining < HOUR_MS ? 'urgent' : 'normal' };
};

const TONE_COLOR: Record<CountdownTone, string> = {
  urgent: 'var(--theme-error-fg)',
  normal: 'var(--theme-card-text)',
  muted: 'var(--theme-muted-text)',
};

/**
 * MyChecklistsPage — a caixa de entrada do gerente em campo (`GET /checklists/my`,
 * assignee OU follower).
 *
 * Mobile-first por definição: esta tela é consumida de pé, no celular, dentro da
 * unidade. Por isso são CARDS (nunca tabela), alvo de toque no card inteiro e
 * uma única informação decisiva em destaque — quanto tempo falta.
 *
 * Ordenação: primeiro o que ainda dá para preencher (pending/in_progress), do
 * prazo mais apertado para o mais folgado; depois o histórico, mais recente
 * primeiro. Quem tem 8 checklists no dia abre a tela e o próximo já está no topo.
 *
 * `unitName` NÃO vem no payload (o `ChecklistsRepository.mapToResponse` deixa os
 * campos de Two-Phase Fetch vazios) — resolvido client-side por
 * `useUnitsDropdown`, exatamente como a `ChecklistsPage` da Task 15 faz.
 */
export const MyChecklistsPage: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAuth();
  const currentUserId = state.user?.id;

  const { data, isLoading, error } = useMyChecklists(MY_CHECKLISTS_QUERY);
  const { data: unitOptions } = useUnitsDropdown();

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), MINUTE_MS);
    return () => window.clearInterval(timer);
  }, []);

  const unitMap = useMemo(() => new Map((unitOptions ?? []).map((unit) => [unit.value, unit.label])), [unitOptions]);

  /**
   * Partição client-side: "A fazer" (pending/in_progress, prazo mais apertado
   * primeiro; sem prazo no fim, para não competir com o que vence hoje) e
   * "Histórico" (o resto, mais recente primeiro).
   *
   * A separação é visual E funcional: sem ela, o gerente com 30 checklists
   * aprovados no mês precisa caçar o único pendente no meio da lista.
   */
  const { openItems, historyItems } = useMemo(() => {
    const list = data?.items ?? [];
    const open = list
      .filter((item) => OPEN_STATUSES.has(item.status))
      .sort((a, b) => {
        const aDue = a.expiresAt ? new Date(a.expiresAt).getTime() : Number.MAX_SAFE_INTEGER;
        const bDue = b.expiresAt ? new Date(b.expiresAt).getTime() : Number.MAX_SAFE_INTEGER;
        return aDue - bDue;
      });
    const history = list
      .filter((item) => !OPEN_STATUSES.has(item.status))
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    return { openItems: open, historyItems: history };
  }, [data]);

  const loadedCount = (data?.items ?? []).length;
  const totalCount = data?.total ?? loadedCount;
  const isTruncated = totalCount > loadedCount;

  /**
   * `/my` também devolve checklists em que o usuário é apenas SEGUIDOR — esses
   * não podem ser preenchidos (o backend restringe `start`/`submit` ao assignee),
   * então vão direto para a revisão read-only em vez de bater no guard da
   * `ChecklistFillPage` e voltar.
   */
  const handleOpen = (checklist: ChecklistResponse): void => {
    const canFill = OPEN_STATUSES.has(checklist.status) && checklist.assigneeId === currentUserId;
    navigate(canFill ? `/checklists/${checklist.id}/fill` : `/checklists/${checklist.id}`);
  };

  const renderCard = (checklist: ChecklistResponse): React.ReactNode => (
    <MyChecklistCard
      key={checklist.id}
      checklist={checklist}
      unitName={checklist.unitId ? unitMap.get(checklist.unitId) : undefined}
      now={now}
      canFill={OPEN_STATUSES.has(checklist.status) && checklist.assigneeId === currentUserId}
      onOpen={() => handleOpen(checklist)}
    />
  );

  return (
    <div
      className="h-full flex flex-col"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
      data-testid="my-checklists-page"
    >
      <PageToolbar
        data-testid="my-checklists-toolbar"
        left={
          <>
            <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>
              Meus checklists
            </span>
            {openItems.length > 0 && (
              <span
                className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
                style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
                data-testid="my-checklists-count"
              >
                {openItems.length}
              </span>
            )}
          </>
        }
      />

      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-4 sm:py-6">
          {error && (
            <div
              className="rounded-lg p-4"
              style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}
              data-testid="my-checklists-error"
            >
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar seus checklists: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando seus checklists..." />}

          {!isLoading && !error && loadedCount === 0 && (
            <ListPageEmpty
              icon="check-square"
              message="Nenhum checklist para você"
              description="Assim que um checklist for atribuído a você, ele aparece aqui."
            />
          )}

          {!isLoading && !error && loadedCount > 0 && (
            <>
              <section data-testid="my-checklists-open">
                <SectionHeader title="A fazer" count={openItems.length} />
                {openItems.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {openItems.map(renderCard)}
                  </div>
                ) : (
                  <p
                    className="text-[13px] rounded-xl px-4 py-6 text-center"
                    style={{
                      color: 'var(--theme-muted-text)',
                      backgroundColor: 'var(--theme-surface-sunken)',
                    }}
                    data-testid="my-checklists-open-empty"
                  >
                    Tudo em dia — nenhum checklist pendente.
                  </p>
                )}
              </section>

              {historyItems.length > 0 && (
                <section className="mt-6" data-testid="my-checklists-history">
                  <SectionHeader title="Histórico" count={historyItems.length} />
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {historyItems.map(renderCard)}
                  </div>
                </section>
              )}

              {isTruncated && (
                <p
                  className="text-xs text-center mt-4"
                  style={{ color: 'var(--theme-muted-text)' }}
                  data-testid="my-checklists-truncated"
                >
                  Mostrando {loadedCount} de {totalCount} checklists.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

MyChecklistsPage.displayName = 'MyChecklistsPage';

/* ------------------------------------------------------------------ */
/* Cabeçalho de seção                                                  */
/* ------------------------------------------------------------------ */

const SectionHeader: React.FC<{ title: string; count: number }> = ({ title, count }) => (
  <div className="flex items-center gap-2 mb-2">
    <h2 className="text-[13px] font-bold uppercase tracking-wider" style={{ color: 'var(--theme-muted-text)' }}>
      {title}
    </h2>
    <span
      className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums"
      style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
    >
      {count}
    </span>
  </div>
);

/* ------------------------------------------------------------------ */
/* Card                                                                */
/* ------------------------------------------------------------------ */

interface MyChecklistCardProps {
  checklist: ChecklistResponse;
  unitName?: string;
  now: number;
  canFill: boolean;
  onOpen: () => void;
}

const MyChecklistCard: React.FC<MyChecklistCardProps> = ({ checklist, unitName, now, canFill, onOpen }) => {
  const meta = CHECKLIST_STATUS_META[checklist.status];
  // A contagem só faz sentido enquanto o checklist é acionável — depois de
  // enviado/aprovado o prazo virou história e não deve piscar em vermelho.
  const countdown = OPEN_STATUSES.has(checklist.status)
    ? formatCountdown(checklist.expiresAt, now)
    : { label: 'Concluído', tone: 'muted' as CountdownTone };

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left rounded-xl p-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring-color)]"
      style={{
        minHeight: 96,
        backgroundColor: 'var(--theme-card-background)',
        border: '1px solid var(--theme-card-border)',
      }}
      data-testid={`my-checklist-card-${checklist.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-[15px] font-semibold leading-snug" style={{ color: 'var(--theme-card-text)' }}>
          {checklist.templateSnapshot.name}
        </h3>
        <StatusPill
          variant={meta.variant}
          label={meta.label}
          className="flex-none"
          data-testid={`my-checklist-status-${checklist.id}`}
        />
      </div>

      <p className="text-[13px] mt-1 truncate" style={{ color: 'var(--theme-muted-text)' }}>
        {unitName ?? 'Sem unidade'}
      </p>

      <div className="flex items-center justify-between gap-2 mt-3">
        <span
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold"
          style={{ color: TONE_COLOR[countdown.tone] }}
          data-testid={`my-checklist-countdown-${checklist.id}`}
        >
          <Icon name="clock" className="w-4 h-4 flex-none" />
          {countdown.label}
        </span>
        <span
          className="inline-flex items-center gap-1 text-[13px] font-semibold flex-none"
          style={{ color: 'var(--theme-primary)' }}
        >
          {canFill ? 'Preencher' : 'Ver'}
          <Icon name="chevron-right" className="w-4 h-4" />
        </span>
      </div>
    </button>
  );
};
